import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HeartPulse, RefreshCw } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { LoadingState, ErrorState } from '../../components/ui/States';
import { useAuth, authHeaders } from '../../auth';
import { formatRelative } from '../../relativeTime';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;
// Auto-refresh within the requested 15-30s range - modest enough not to be
// "aggressive" polling, frequent enough that a service outage shows up
// without the admin needing to remember to click Refresh.
const REFRESH_INTERVAL_MS = 20000;

// Presentation-level only - the backend's raw mqtt.status value ('connected'/
// 'disconnected') is untouched, this just maps it to French for display.
const MQTT_STATUS_LABELS = { connected: 'Connecté', disconnected: 'Déconnecté' };

function StatusRow({ label, ok, detail }) {
  return (
    <div className="summary-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div>
        <div className="summary-label">{label}</div>
        {detail && <div className="summary-sub">{detail}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={`status-dot status-dot-${ok ? 'online' : 'offline'}`}></span>
        <strong>{ok ? 'OK' : 'ERREUR'}</strong>
      </div>
    </div>
  );
}

export default function SystemHealthPage() {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Guards against overlapping requests if a manual Refresh click lands
  // mid-poll, or a fetch takes longer than the interval for once.
  const inFlightRef = useRef(false);

  const load = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await fetch(`${API_BASE}/admin/system-health`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error('load failed');
      setHealth(await res.json());
      setError(false);
    } catch {
      // A failed refresh does not wipe already-known health data - it's
      // reported as a banner instead, so the page never silently reverts a
      // real "everything OK" reading to "unknown" just because one poll
      // request hiccuped.
      setError(true);
    } finally {
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  // health.devices.list already carries each device's `type` (see
  // buildDeviceList() in server.js) - the panel/weather split is derived
  // here rather than adding a new backend field for numbers the frontend
  // can already compute from data it has.
  const deviceTypeCounts = useMemo(() => {
    const list = health?.devices?.list || [];
    return {
      panels: list.filter((d) => d.type === 'panel').length,
      weather: list.filter((d) => d.type === 'weather').length
    };
  }, [health]);

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'État du Système' }]} />
      <div className="page-header">
        <h1><HeartPulse size={22} /> État du Système</h1>
        <button className="btn-secondary" onClick={load}><RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Actualiser</button>
      </div>
      {/* Phase 3.2: this page can only ever run while the platform is
          reachable - it cannot detect the platform itself being down
          (server/Docker/nginx/network outage). That is the job of the
          separate external monitor described in monitoring/README.md. */}
      <p className="summary-sub" style={{ marginTop: -8, marginBottom: 16 }}>
        Diagnostic interne détaillé - disponible uniquement lorsque la plateforme est déjà joignable.
        Pour la détection d'une panne totale du serveur, voir la supervision externe (monitoring/README.md).
      </p>

      {health === null && !error ? (
        <LoadingState label="Vérification en cours..." />
      ) : health === null && error ? (
        <ErrorState label="Impossible de récupérer l'état du système" />
      ) : (
        <>
          {error && (
            <div className="summary-card" style={{ marginBottom: 16, borderLeft: '3px solid #c0392b' }}>
              Dernière actualisation échouée - les valeurs ci-dessous datent de la dernière vérification réussie.
            </div>
          )}

          <div className="section">
            <h3 className="section-title">Services</h3>
            <StatusRow label="Backend" ok={health.backend.status === 'healthy'} detail={`Uptime: ${Math.floor(health.backend.uptimeSeconds / 60)} min`} />
            <StatusRow label="MQTT / EMQX" ok={health.mqtt.status === 'connected'} detail={MQTT_STATUS_LABELS[health.mqtt.status] || health.mqtt.status} />
            <StatusRow label="InfluxDB" ok={health.influx.status === 'connected'} detail={health.influx.status === 'connected' ? undefined : health.influx.error} />
          </div>

          <div className="section">
            <h3 className="section-title">Appareils</h3>
            <div className="summary-grid summary-grid-compact">
              <div className="summary-card">
                <div className="summary-label">Total</div>
                <div className="summary-value">{health.devices.total}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">En ligne</div>
                <div className="summary-value">{health.devices.online}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Hors ligne</div>
                <div className="summary-value">{health.devices.offline}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Panneaux</div>
                <div className="summary-value">{deviceTypeCounts.panels}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Stations météo</div>
                <div className="summary-value">{deviceTypeCounts.weather}</div>
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section-title">Dernière télémétrie</h3>
            <div className="summary-grid summary-grid-compact">
              <div className="summary-card">
                <div className="summary-label">Panneaux</div>
                <div className="summary-value" style={{ fontSize: '1.1rem' }}>{formatRelative(health.lastPanelTelemetry, now)}</div>
                {health.lastPanelTelemetry && <div className="summary-sub">{new Date(health.lastPanelTelemetry).toLocaleString('fr-FR')}</div>}
              </div>
              <div className="summary-card">
                <div className="summary-label">Météo</div>
                <div className="summary-value" style={{ fontSize: '1.1rem' }}>{formatRelative(health.lastWeatherTelemetry, now)}</div>
                {health.lastWeatherTelemetry && <div className="summary-sub">{new Date(health.lastWeatherTelemetry).toLocaleString('fr-FR')}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
